import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';

const DemoSection = styled.section`
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.backgroundSecondary};
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const SectionLabel = styled(motion.span)`
  display: inline-block;
  padding: 8px 16px;
  background: ${props => props.theme.gradients.subtle};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 100px;
  font-size: 0.875rem;
  font-weight: ${props => props.theme.typography.weights.medium};
  color: ${props => props.theme.colors.accentPurple};
  margin-bottom: ${props => props.theme.spacing.md};
  letter-spacing: 0.02em;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: ${props => props.theme.typography.weights.extrabold};
  margin-bottom: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: clamp(0.9375rem, 1.5vw, 1.0625rem);
  color: ${props => props.theme.colors.subtextGray};
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.5;
`;

const VideoWrapper = styled(motion.div)`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  box-shadow: ${props => props.theme.shadows.large};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.theme.gradients.primary};
    z-index: 2;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    border-radius: 16px;
  }
`;

const Video = styled.video`
  width: 100%;
  display: block;
  border-radius: 20px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    border-radius: 16px;
  }
`;

const ControlsOverlay = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  display: flex;
  gap: 8px;
  z-index: 10;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    bottom: 12px;
    right: 12px;
  }
`;

const ControlButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.05);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 36px;
    height: 36px;
  }
`;

const PlayingIndicator = styled(motion.div)`
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 500;
  color: white;
  z-index: 10;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    top: 12px;
    left: 12px;
    padding: 6px 10px;
    font-size: 0.6875rem;
  }
`;

const LiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22C55E;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

// Icons
const MuteIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M11 5L6 9H2v6h4l5 4V5z" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="23" y1="9" x2="17" y2="15" strokeLinecap="round" />
        <line x1="17" y1="9" x2="23" y2="15" strokeLinecap="round" />
    </svg>
);

const UnmuteIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M11 5L6 9H2v6h4l5 4V5z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" strokeLinecap="round" />
    </svg>
);

const FullscreenIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ProductDemo = () => {
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const isInView = useInView(containerRef, { amount: 0.5 });
    const [isMuted, setIsMuted] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        if (isInView) {
            video.play().catch(() => { });
            setIsPlaying(true);
        } else {
            video.pause();
            setIsPlaying(false);
        }
    }, [isInView]);

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(!isMuted);
        }
    };

    const toggleFullscreen = () => {
        if (videoRef.current) {
            if (videoRef.current.requestFullscreen) {
                videoRef.current.requestFullscreen();
            } else if (videoRef.current.webkitEnterFullscreen) {
                videoRef.current.webkitEnterFullscreen();
            }
        }
    };

    return (
        <DemoSection ref={containerRef}>
            <Container>
                <SectionHeader>
                    <SectionLabel
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        Feature Demo
                    </SectionLabel>
                    <SectionTitle
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        NEAL — the AI Assistant
                    </SectionTitle>
                    <SectionSubtitle
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        See how NEAL coaches teams through A3 problem solving inside the platform.
                    </SectionSubtitle>
                </SectionHeader>

                <VideoWrapper
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                >
                    {isPlaying && (
                        <PlayingIndicator
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <LiveDot />
                            Playing
                        </PlayingIndicator>
                    )}

                    <Video
                        ref={videoRef}
                        src="https://mdzidvfxuhwkzmdptlub.supabase.co/storage/v1/object/public/Content/VIDEO-2026-01-22-18-33-39.mov"
                        muted
                        loop
                        playsInline
                        preload="metadata"
                    />

                    <ControlsOverlay>
                        <ControlButton onClick={toggleMute} aria-label={isMuted ? 'Unmute' : 'Mute'}>
                            {isMuted ? <MuteIcon /> : <UnmuteIcon />}
                        </ControlButton>
                        <ControlButton onClick={toggleFullscreen} aria-label="Fullscreen">
                            <FullscreenIcon />
                        </ControlButton>
                    </ControlsOverlay>
                </VideoWrapper>
            </Container>
        </DemoSection>
    );
};

export default ProductDemo;
